import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const getStripePublishableKey = createServerFn({ method: "GET" }).handler(async () => {
  const key = process.env.STRIPE_PUBLISHABLE_KEY;
  if (!key) throw new Error("Stripe is not configured");
  return { publishableKey: key };
});

type PlanId = "starter" | "pro" | "unlimited";


const PLAN_PRICING: Record<
  PlanId,
  { amountCents: number; name: string; description: string; lookupKey: string }
> = {
  starter: {
    amountCents: 999,
    name: "Lumvio Starter",
    description: "5 projects / month",
    lookupKey: "lumvio_starter_monthly_v1",
  },
  pro: {
    amountCents: 1999,
    name: "Lumvio Pro",
    description: "15 projects / month",
    lookupKey: "lumvio_pro_monthly_v1",
  },
  unlimited: {
    amountCents: 3499,
    name: "Lumvio Unlimited",
    description: "Unlimited projects",
    lookupKey: "lumvio_unlimited_monthly_v1",
  },
};

type StripeClient = import("stripe").default;

async function getStripe(): Promise<StripeClient> {
  const secret = process.env.STRIPE_SECRET_KEY;
  if (!secret) throw new Error("Stripe is not configured");
  const Stripe = (await import("stripe")).default;
  return new Stripe(secret, { apiVersion: "2025-01-27.acacia" as never });
}

async function getOrCreatePriceId(stripe: StripeClient, plan: PlanId): Promise<string> {
  const cfg = PLAN_PRICING[plan];
  const existing = await stripe.prices.list({
    lookup_keys: [cfg.lookupKey],
    active: true,
    limit: 1,
    expand: ["data.product"],
  });
  if (existing.data[0]) return existing.data[0].id;

  const productList = await stripe.products.search({
    query: `metadata['lumvio_plan']:'${plan}'`,
    limit: 1,
  });
  const product =
    productList.data[0] ??
    (await stripe.products.create({
      name: cfg.name,
      description: cfg.description,
      metadata: { lumvio_plan: plan },
    }));

  const price = await stripe.prices.create({
    product: product.id,
    unit_amount: cfg.amountCents,
    currency: "usd",
    recurring: { interval: "month" },
    lookup_key: cfg.lookupKey,
    metadata: { lumvio_plan: plan },
  });
  return price.id;
}

async function getOrCreateCustomerId(
  stripe: StripeClient,
  userId: string,
  email: string | undefined,
  fullName: string | null | undefined,
  existing: string | null,
): Promise<string> {
  if (existing) {
    try {
      const c = await stripe.customers.retrieve(existing);
      if (!(c as { deleted?: boolean }).deleted) return existing;
    } catch {
      /* fallthrough to create */
    }
  }
  const created = await stripe.customers.create({
    email,
    name: fullName ?? undefined,
    metadata: { user_id: userId },
  });
  return created.id;
}

export const createSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { plan: PlanId }) => {
    if (!input?.plan || !(input.plan in PLAN_PRICING)) throw new Error("Invalid plan");
    return input;
  })
  .handler(async ({ data, context }) => {
    const { userId, claims, supabase } = context;
    const email = (claims as { email?: string } | undefined)?.email;

    const { data: profile } = await supabase
      .from("profiles")
      .select("stripe_customer_id, stripe_subscription_id, full_name, role")
      .eq("id", userId)
      .maybeSingle();

    // Subscriptions are freelancer-only. Enforce server-side, not just UI.
    if ((profile as { role?: string } | null)?.role !== "freelancer") {
      throw new Error("Subscriptions are only available for freelancer accounts.");
    }

    const stripe = await getStripe();
    const priceId = await getOrCreatePriceId(stripe, data.plan);
    const customerId = await getOrCreateCustomerId(
      stripe,
      userId,
      email,
      (profile as { full_name: string | null } | null)?.full_name ?? null,
      (profile as { stripe_customer_id: string | null } | null)?.stripe_customer_id ?? null,
    );

    // Persist customer id via admin (bypasses profile column protections)
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("profiles")
      .update({ stripe_customer_id: customerId } as never)
      .eq("id", userId);

    // If an existing subscription is still around, reuse or handle it.
    const existingSubId = (profile as { stripe_subscription_id: string | null } | null)?.stripe_subscription_id;
    if (existingSubId) {
      try {
        const existing = await stripe.subscriptions.retrieve(existingSubId, {
          expand: ["latest_invoice.payment_intent"],
        });
        const currentPrice = existing.items.data[0]?.price?.id;
        // Already active on the SAME plan — no new checkout needed.
        if (["active", "trialing"].includes(existing.status) && currentPrice === priceId) {
          return {
            alreadyActive: true as const,
            subscriptionId: existing.id,
            currentPeriodEnd: (existing as unknown as { current_period_end: number }).current_period_end,
            cancelAtPeriodEnd: existing.cancel_at_period_end,
          };
        }
        if (["incomplete", "past_due"].includes(existing.status)) {
          if (currentPrice === priceId) {
            const inv = existing.latest_invoice as unknown as
              | (import("stripe").Stripe.Invoice & { payment_intent: import("stripe").Stripe.PaymentIntent | string | null })
              | null;
            const pi = (inv?.payment_intent ?? null) as import("stripe").Stripe.PaymentIntent | null;
            if (pi?.client_secret) {
              return {
                clientSecret: pi.client_secret,
                subscriptionId: existing.id,
                publishableKey: process.env.STRIPE_PUBLISHABLE_KEY!,
              };
            }
          } else {
            // Plan changed — kill the old incomplete sub so we don't charge the stale invoice.
            try {
              await stripe.subscriptions.cancel(existingSubId, { invoice_now: false, prorate: false });
            } catch {
              /* ignore */
            }
          }
        }
      } catch {
        /* fall through to create new */
      }
    }


    const subscription = await stripe.subscriptions.create({
      customer: customerId,
      items: [{ price: priceId }],
      payment_behavior: "default_incomplete",
      payment_settings: {
        save_default_payment_method: "on_subscription",
      },
      expand: ["latest_invoice.payment_intent"],
      metadata: { user_id: userId, plan: data.plan, kind: "subscription" },
    });

    const invoice = subscription.latest_invoice as unknown as
      | (import("stripe").Stripe.Invoice & { payment_intent: import("stripe").Stripe.PaymentIntent | string | null })
      | null;
    const pi = (invoice?.payment_intent ?? null) as import("stripe").Stripe.PaymentIntent | null;
    if (!pi?.client_secret) throw new Error("Unable to initialize payment");

    await supabaseAdmin
      .from("profiles")
      .update({ stripe_subscription_id: subscription.id, subscription_status: subscription.status } as never)
      .eq("id", userId);

    return {
      clientSecret: pi.client_secret,
      subscriptionId: subscription.id,
      publishableKey: process.env.STRIPE_PUBLISHABLE_KEY!,
    };
  });

export const createBillingPortal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId, supabase } = context;
    const { data: profile } = await supabase
      .from("profiles")
      .select("stripe_customer_id")
      .eq("id", userId)
      .maybeSingle();
    const customerId = (profile as { stripe_customer_id: string | null } | null)?.stripe_customer_id;
    if (!customerId) throw new Error("No billing account yet — subscribe to a plan first.");

    const stripe = await getStripe();
    const origin =
      getRequestHeader("origin") ||
      (getRequestHeader("referer") ? new URL(getRequestHeader("referer")!).origin : "");

    const session = await stripe.billingPortal.sessions.create({
      customer: customerId,
      return_url: `${origin}/pricing`,
    });
    return { url: session.url };
  });
